import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { api } from './lib/api';
import { NewsletterForm } from './components/blog/NewsletterForm';
import { BlogFooter } from './components/blog/BlogFooter';
import { HeroCaption } from './components/blog/HeroCaption';
import { SiteHeader } from './components/SiteHeader';
import { StartPlanningButton } from './components/StartPlanningButton';
import { useAuth } from './hooks/useAuth';
import { canSeeAnder } from './lib/anderAccess';
import { TravelerStories } from './components/TravelerStories';
import { Heading, Icon } from './components/ui';
import type { BlogPostSummary } from '@wanderlust/shared';
import { trackEvent } from './lib/analytics';
import { blogCoverAlt } from './lib/blogFormatters';
import { blogImageSrcSet } from './lib/blogImages';
import {
  HOME_DESCRIPTION,
  HOME_JSON_LD_ID,
  HOME_TITLE,
  SITE_NAME,
  buildHomeJsonLd,
} from './lib/homeJsonLd';
import { setCanonicalUrl, setMetaTag } from './lib/documentSeo';
import { HomeDataContext } from './contexts/HomeDataContext';
import { HOME_BLOG_POST_COUNT, readPrerenderedHomePosts } from './lib/prerender';
import { blogPostLinkState, prefetchBlogPost } from './lib/blogPostCache';

function applyHomeSeo() {
  const canonicalUrl = `${window.location.origin}/`;
  document.title = HOME_TITLE;
  setMetaTag('name', 'description', HOME_DESCRIPTION);
  setMetaTag('name', 'robots', 'index, follow');
  setMetaTag('property', 'og:type', 'website');
  setMetaTag('property', 'og:title', HOME_TITLE);
  setMetaTag('property', 'og:description', HOME_DESCRIPTION);
  setMetaTag('property', 'og:url', canonicalUrl);
  setMetaTag('property', 'og:site_name', SITE_NAME);
  setMetaTag('name', 'twitter:card', 'summary_large_image');
  setMetaTag('name', 'twitter:title', HOME_TITLE);
  setMetaTag('name', 'twitter:description', HOME_DESCRIPTION);
  setCanonicalUrl(canonicalUrl);

  let structuredData = document.head.querySelector(`#${HOME_JSON_LD_ID}`);
  if (!structuredData) {
    structuredData = document.createElement('script');
    structuredData.setAttribute('id', HOME_JSON_LD_ID);
    structuredData.setAttribute('type', 'application/ld+json');
    document.head.appendChild(structuredData);
  }

  structuredData.textContent = buildHomeJsonLd(window.location.origin);
}

const HERO_PHOTO_CREDIT =
  'Lake Iseo, Italy. Photo by [Isaac Maffeis](https://unsplash.com/@isaacmaffeis?utm_source=wanderlust_cravings&utm_medium=referral) on [Unsplash](https://unsplash.com/?utm_source=wanderlust_cravings&utm_medium=referral)';

/**
 * The photograph behind the masthead, plus the scrims that make type legible
 * on top of it.
 *
 * Three gradients, each doing one job: the horizontal one holds the left third
 * at full navy so the headline sits on flat colour and the picture emerges
 * where the copy ends; the top one darkens the band the nav sits in, since
 * those links are white on whatever the photo happens to be doing up there;
 * the bottom one lands the credit and softens the edge into the next section.
 *
 * Phones don't get it. The copy fills that screen on its own, and a photo
 * behind it would be noise under a scrim heavy enough to keep the type
 * readable. The 1x1 data URI below md is what keeps a phone from downloading
 * 140KB it will never show — a display:none <img> is still fetched.
 */
function HeroBackdrop() {
  return (
    <figure className="absolute inset-0 hidden md:block">
      <picture>
        <source
          media="(max-width: 767px)"
          srcSet="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
        />
        <source
          srcSet="/hero-lake-iseo-1000w.webp 1000w, /hero-lake-iseo.webp 1600w"
          sizes="100vw"
          type="image/webp"
        />
        {/* Decorative: the headline says everything this says. Anchored right
            of centre so a narrower viewport crops the sea, not the village. */}
        <img
          src="/hero-lake-iseo.jpg"
          srcSet="/hero-lake-iseo-1000w.jpg 1000w, /hero-lake-iseo.jpg 1600w"
          sizes="100vw"
          alt=""
          width="1600"
          height="900"
          fetchPriority="high"
          className="h-full w-full object-cover object-[65%_50%]"
        />
      </picture>
      {/* Flat navy out to 45%, which is roughly where the copy ends at every
          width the picture shows at, then the village comes up through the
          fade. Percentages of the band, so it tracks the copy without a
          breakpoint of its own. */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary from-45% via-primary/55 via-72% to-primary/0" />
      <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-primary/95 via-primary/70 via-40% to-primary/0" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-primary/85 to-primary/0" />
      <HeroCaption caption={HERO_PHOTO_CREDIT} tone="inverse" className="absolute bottom-3 right-6" />
    </figure>
  );
}

/**
 * The hero's ask: one button into the intake form, whose first question is the
 * trip itself. It used to be that question asked here, as a text field; a
 * button asks nothing of someone who has not decided yet, which the headline
 * says is the point. The press is where the Dreams funnel starts.
 */
function HeroCta() {
  return (
    <div>
      <StartPlanningButton source="home_hero" />
      {/* The intake form doesn't need a destination, so the hero says so
          before someone decides they aren't ready to press. */}
      <p className="mt-4 text-sm text-white/70">
        Not sure where yet? That’s a fine place to start.
      </p>
    </div>
  );
}

/**
 * The masthead: the header sits over this band rather than above it, so the
 * photograph runs to the top of the page and the lockup lands on it. That's
 * what the top padding is buying back — the header is out of the flow, so the
 * hero owes it its own height (80px, 128 from sm) before the headline starts.
 *
 * The copy is capped near 60 characters rather than run to the 1200px rail:
 * that's the same measure the blog holds its article headers to, and here it
 * also keeps the words inside the flat-navy part of the scrim.
 */
function Hero() {
  const { user } = useAuth();
  return (
    <section className="relative isolate overflow-hidden bg-primary pt-32 pb-24 sm:pt-48 sm:pb-32" aria-labelledby="home-heading">
      <HeroBackdrop />
      <div className="relative max-w-[1200px] mx-auto px-6">
        <div className="max-w-[760px]">
          <Heading level={1} id="home-heading" weight="regular" color="inverse" className="text-4xl sm:text-5xl lg:text-6xl mb-6 sm:mb-8 leading-tight text-balance">
            Let’s figure out where you should go next.
          </Heading>
          <p className="text-white/75 text-lg leading-relaxed max-w-[500px] mb-8 sm:mb-10">
            I’m Ken. I plan and book trips, from finding the right hotel to sorting out the logistics that make the whole thing work.
          </p>
          <HeroCta />
          {/* The other door: Ander, the planning assistant, shapes the trip in
              a conversation before anything is sent. Same brief, same advisor.
              Admins only until it is announced; the prerendered page has no
              user, so it never carries the line. */}
          {canSeeAnder(user?.role) && (
            <p className="mt-5 text-sm text-white/70">
              Or try{' '}
              <Link to="/ander" className="font-medium text-white underline underline-offset-2 hover:text-accent transition-colors">
                Ander, my planning assistant
              </Link>
              , first. Describe the trip and see a first shape before you send anything.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * The header's About link lands here (`/#about`), from this page or any other.
 * The header is out of the flow rather than sticky, so the section's own top
 * padding is all the room the heading needs once it is scrolled to.
 */
function About() {
  return (
    <section id="about" className="py-28 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div className="flex justify-center md:justify-start">
            <picture>
              <source srcSet="/me.webp" type="image/webp" />
              <img src="/me.jpeg" alt="Ken McGrady, travel advisor" width="698" height="900" loading="lazy" decoding="async" className="w-full max-w-[450px] aspect-[4/5] object-cover" />
            </picture>
          </div>
          <div>
            <Heading level={2} weight="semibold" color="primary" className="text-4xl mb-6">
              Hi, I’m Ken
            </Heading>
            <p className="text-neutral-600 mb-5 text-lg leading-relaxed">
              I’m a travel advisor. I mostly plan trips for couples, families, and small groups.
            </p>
            <p className="text-neutral-600 mb-5 text-lg leading-relaxed">
              I care a lot about where you stay. Not just which hotel, but which room, and whether the one facing the garden is worth the extra $80 a night. Sometimes it is. Often it isn’t, and I’ll say so.
            </p>
            <p className="text-neutral-600 mb-5 text-lg leading-relaxed">
              Food, nature, and local culture are usually where I spend the most time researching. I also pay attention to the boring parts, like how long it really takes to get between places or whether a connection looks too tight on paper.
            </p>
            <p className="text-neutral-600 mb-5 text-lg leading-relaxed">
              I can help figure out the trip, book it, and stay involved when plans change.
            </p>
            <Link
              to="/plan"
              onClick={() => trackEvent('funnel-cta-click', { location: 'about' })}
              className="inline-block mt-4 px-8 py-3.5 border-2 border-primary text-primary text-sm font-medium uppercase tracking-wide rounded hover:bg-primary hover:text-white transition-all"
            >
              Tell Me About Your Trip
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

const planningSteps = [
  {
    icon: 'Compass' as const,
    title: "Tell me about your trip",
    description: "A destination, rough dates, or even just the kind of trip you want is enough.",
  },
  {
    icon: 'MessagesSquare' as const,
    title: "We figure out what fits",
    description: "We’ll talk through what matters to you, what feels realistic, and where you want my help.",
  },
  {
    icon: 'Map' as const,
    title: "I plan and book it",
    description: "I research the options, put the trip together, handle the reservations, and stay involved if plans change.",
  },
];

/**
 * Three of the tools, as a name apiece. The names are specific enough to say
 * what they do, and the screenshot beside them shows one of them doing it, so
 * a line of explanation under each would buy nothing for its length.
 */
const wanderNerdTools = [
  { name: 'Flight Reliability', href: 'https://wandernerd.com/flights', icon: 'Plane' as const },
  { name: 'Connection Risk', href: 'https://wandernerd.com/connections', icon: 'ArrowLeftRight' as const },
  { name: 'Cruise Crowds', href: 'https://wandernerd.com/cruise-crowds', icon: 'Anchor' as const },
];

/**
 * Hover moves the frame, not the words. Darkening or lightening the label
 * would make the hover state the hardest one to read, which is the trap the
 * design system calls out for gold. The chips stay white on the tint, so the
 * hover is the border going full gold and nothing else.
 */
const WANDERNERD_CHIP_CLASS =
  'inline-flex items-center gap-2 rounded-pill bg-white border border-accent/40 ' +
  'px-4 py-2 text-sm font-medium text-primary hover:border-accent transition-colors';

/**
 * The screenshot's slot: the full content width on a phone, half the container
 * (less its gutter) on a tablet, and a fixed 536px from 1200px up, where
 * `max-w-[1200px] px-6` and `md:gap-20` stop the column growing.
 */
const WANDERNERD_SHOT_SIZES =
  '(max-width: 767px) calc(100vw - 48px), (max-width: 1199px) calc(50vw - 64px), 536px';

/**
 * The tools Ken builds, and why a travel advisor building them matters.
 *
 * WanderNerd is the same author's site of free, data-driven travel tools. The
 * global footer already links to it, which says the two exist; this section
 * says what the connection is for. The argument is the specific one: the trip
 * decisions he makes for clients (which connection, which flight, which port
 * day) run on the same records the tools are built from.
 *
 * Sits near the end of the page, after the blog and just above the newsletter
 * strip: the page leads with the advisory work, and this is the side project a
 * reader meets once they have seen that. A light gold tint rather than the
 * navy: a dark band here would sit one grey strip away from the closing ask
 * and take the weight off it. The tint is barely apart from the grey bands on
 * either side (about 1.06:1), so a gold hairline above and below does the
 * separating, and the newsletter strip under it needs no rule of its own.
 *
 * The screenshot is one real flight read by three of the tools, not a mock-up
 * of one, because the claim being made is that these tools exist and are
 * specific. Three answers to the same flight say "a collection" the way one
 * tool's answer cannot, which is the claim the chips beside it make. The two
 * lists are deliberately not the same three: the picture happens to show
 * Window Seat, the chips link the ones a trip decision turns on. A checked-in
 * picture does go stale as the tools change, so the caption says what it is,
 * and nothing in the surrounding copy leans on a number inside it: the link,
 * not the image, is what stays current.
 *
 * The tools themselves are three chips above the button rather than a card
 * apiece below it. A card block asked for a third of the section to say what
 * the paragraph beside it had already said, and it sat past the call to
 * action, so the reader met the roster after being asked to leave. As names
 * on the way to the button they cost a line and still deep-link.
 */
function WanderNerdTools() {
  return (
    <section className="py-28 bg-accent/15 border-y border-accent/30" aria-labelledby="wandernerd-heading">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div>
            <Heading level={2} id="wandernerd-heading" weight="semibold" color="primary" className="text-4xl mb-6">
              I also build WanderNerd
            </Heading>
            <p className="text-neutral-600 mb-5 text-lg leading-relaxed">
              WanderNerd is my other project: free tools built from the flight, connection, and
              crowd data that travel advice should rely on.
            </p>
            <p className="text-neutral-600 mb-6 text-lg leading-relaxed">
              It’s also why I plan differently. When I flag a tight layover or a crowded port day,
              it isn’t a hunch. I’ve built the tools to check.
            </p>
            {/* Wraps rather than scrolls: the three fit on one line in the
                536px column, and on a narrower one they stack instead of
                being clipped. */}
            <ul className="flex flex-wrap gap-2 mb-8">
              {wanderNerdTools.map((tool) => (
                <li key={tool.name}>
                  <a
                    href={tool.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={WANDERNERD_CHIP_CLASS}
                  >
                    {/* The darker gold: the brand gold is 2:1 on the tint,
                        under the 3:1 an icon needs. */}
                    <Icon name={tool.icon} size="sm" className="text-accent-strong" strokeWidth={1.75} />
                    {tool.name}
                  </a>
                </li>
              ))}
            </ul>
            <a
              href="https://wandernerd.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-3.5 border-2 border-primary text-primary text-sm font-medium uppercase tracking-wide rounded hover:bg-primary hover:text-white transition-all"
            >
              Explore the free tools
            </a>
          </div>
          <figure className="m-0">
            {/* The screenshot carries its own light ground, so the frame is a
                hairline and a shadow rather than a padded white card, which
                would read as a second border inside the first. */}
            <div className="rounded-surface overflow-hidden border border-accent/30 shadow-lg">
              <picture>
                <source
                  srcSet="/wandernerd-answer-520w.webp 520w, /wandernerd-answer.webp 1040w"
                  sizes={WANDERNERD_SHOT_SIZES}
                  type="image/webp"
                />
                <img
                  src="/wandernerd-answer.png"
                  srcSet="/wandernerd-answer-520w.png 520w, /wandernerd-answer.png 1040w"
                  sizes={WANDERNERD_SHOT_SIZES}
                  alt="WanderNerd reading one real flight, Delta 738 from New York to Los Angeles, through three tools: Flight Reliability, 76% of its arrivals within 15 minutes of schedule; Connection Risk, 16% of them at least 30 minutes late, enough to eat a 60-minute layover; and Window Seat, the left side by a slight edge. Every card says what it counted and how far the data runs."
                  width="1040"
                  height="592"
                  loading="lazy"
                  decoding="async"
                  className="block w-full h-auto"
                />
              </picture>
            </div>
            {/* neutral-600, not the usual caption 500, which is 4.3:1 on
                the tint. */}
            <figcaption className="mt-4 text-center text-sm text-neutral-600">
              One real flight, read by three of the tools. Actual results, not a mock-up.
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

/**
 * Sits on the dark band so it reads as the answer to the hero's CTA rather than
 * another content section. Placed after the introduction: who I am, then what
 * working together actually looks like.
 *
 * Each <li> is a row-spanning subgrid, so the number, icon, title and body of
 * every step share a row track and line up across the columns no matter how a
 * title wraps. That replaces the old fixed title min-height.
 */
function PlanningSteps() {
  return (
    <section className="py-32 bg-primary">
      <div className="max-w-[1200px] mx-auto px-6">
        <Heading level={2} weight="semibold" color="inverse" className="text-4xl text-center mb-4">
          What Planning With Me Looks Like
        </Heading>
        <p className="text-center text-white/75 text-lg mb-14 max-w-[720px] mx-auto">
          Start with whatever you know. I’ll help with the rest.
        </p>
        <ol className="grid grid-cols-1 gap-12 tablet:grid-cols-3 tablet:grid-rows-[auto_auto_auto_auto] tablet:gap-y-0">
          {planningSteps.map((step, i) => (
            <li key={step.title} className="tablet:grid tablet:row-span-4 tablet:grid-rows-subgrid">
              <span className="block font-heading text-sm tracking-[0.2em] text-accent/70 mb-2 tablet:mb-4 tablet:text-center" aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </span>
              <div className="relative mb-3 tablet:mb-5 tablet:flex tablet:justify-center">
                {/* Hairline through the icons' center line, sitting behind them.
                    The icons are centered in their columns, so this spans from
                    this column's midpoint to the next one's: left-1/2, then a
                    negative right of half a column plus the gutter (gap-12).
                    It stops at the outer icons rather than overshooting the
                    row. `top-7` is half the icon height and must track it if
                    that changes. */}
                {i < planningSteps.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="hidden tablet:block absolute left-1/2 right-[calc(-50%-3rem)] top-7 h-px bg-white/[0.075]"
                  />
                )}
                <Icon
                  name={step.icon}
                  size="xl"
                  color="accent"
                  className="relative w-14 h-14"
                  strokeWidth={1.5}
                />
              </div>
              <Heading level={3} weight="semibold" color="inverse" className="text-xl mb-2 tablet:mb-3">
                {step.title}
              </Heading>
              <p className="text-white/75 leading-relaxed">
                {step.description}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/**
 * Three angles on the same business rather than three traveler segments:
 * where you stay, why you're going, and what you do once you're there.
 * The descriptions deliberately don't share a sentence shape — three cards
 * built from one template is what makes a page read as written-by-committee.
 * `alt` is tracked per card because the photography is reused across titles
 * and shouldn't be described by whatever the heading happens to say.
 */
const experiences = [
  {
    title: "Great Hotels & Resorts",
    description: "Sometimes the hotel is the reason for the trip. I love figuring out which property, room, and rate make the most sense for what you’re doing.",
    alt: "A couple on a candlelit terrace overlooking a city at dusk",
    image: "/romantic-getaway.jpg",
    webp: "/romantic-getaway.webp",
    image400: "/romantic-getaway-400w.jpg",
    webp400: "/romantic-getaway-400w.webp",
  },
  {
    title: "Celebrations & Getaways",
    description: "Anniversaries, birthdays, honeymoons, family trips, or just a few days away together. If there’s a reason behind the trip, I like making sure it feels like it.",
    alt: "A family walking together along the shore at sunset",
    image: "/family-vacation.jpg",
    webp: "/family-vacation.webp",
    image400: "/family-vacation-400w.jpg",
    webp400: "/family-vacation-400w.webp",
  },
  {
    title: "Food, Nature & Culture",
    description: "This is the part I’m nerdiest about. I love researching where to eat and what to do, especially when there’s good food, scenery, or a place with a strong sense of itself.",
    alt: "Vendors selling food from boats at a floating market",
    image: "/nature-food-culture.jpg",
    webp: "/nature-food-culture.webp",
    image400: "/nature-food-culture-400w.jpg",
    webp400: "/nature-food-culture-400w.webp",
  },
];

function Experiences() {
  return (
    <section className="py-28 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        <Heading level={2} weight="semibold" color="primary" className="text-4xl text-center mb-4">
          Trips I Especially Like Planning
        </Heading>
        <p className="text-center text-neutral-600 text-lg mb-16">
          A few kinds of trips I naturally get nerdy about.
        </p>
        {/* Two columns between md and lg leaves an odd card stranded in its own
            row. `:nth-child(odd):last-child` matches only that case, so the
            orphan spans both tracks and re-centers itself at one column's
            width. Reset at lg, where three columns divide evenly. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:[&>*:nth-child(odd):last-child]:col-span-2 md:[&>*:nth-child(odd):last-child]:mx-auto md:[&>*:nth-child(odd):last-child]:w-[calc(50%-1.25rem)] lg:[&>*:nth-child(odd):last-child]:col-span-1 lg:[&>*:nth-child(odd):last-child]:w-auto">
          {experiences.map((exp) => (
            <div
              key={exp.title}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:-translate-y-2 hover:shadow-xl transition-all"
            >
              <picture>
                <source
                  srcSet={`${exp.webp400} 400w, ${exp.webp} 800w`}
                  sizes="(max-width: 767px) calc(100vw - 48px), (max-width: 1023px) calc(50vw - 50px), 380px"
                  type="image/webp"
                />
                <img
                  src={exp.image}
                  srcSet={`${exp.image400} 400w, ${exp.image} 800w`}
                  sizes="(max-width: 767px) calc(100vw - 48px), (max-width: 1023px) calc(50vw - 50px), 380px"
                  alt={exp.alt}
                  width="800"
                  height="600"
                  loading="lazy"
                  decoding="async"
                  className="w-full aspect-[4/3] object-cover"
                />
              </picture>
              <Heading level={3} weight="semibold" color="primary" className="text-2xl px-6 pt-6 pb-3">
                {exp.title}
              </Heading>
              <p className="text-neutral-600 px-6 pb-7 text-base leading-relaxed">
                {exp.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * The preview card puts the cover in one half of a `max-w-[1200px] px-6`
 * container, so it paints at 576 CSS px from 1200px up, half the viewport
 * between `md` and there, and the full content width on a phone.
 */
const HOME_COVER_SIZES =
  '(max-width: 767px) calc(100vw - 48px), (max-width: 1199px) calc(50vw - 24px), 576px';

/** 576 and 1152 are the desktop slot at 1x and 2x; the rest cover phones and tablets. */
const HOME_COVER_WIDTHS = [400, 576, 768, 1152, 1536];

function BlogPreview({ posts }: { posts: BlogPostSummary[] }) {

  if (posts.length === 0) return null;

  return (
    <section className="py-32 bg-neutral-50">
      <div className="max-w-[1200px] mx-auto px-6">
        <Heading level={2} weight="semibold" color="primary" className="text-4xl text-center mb-4">
          From the Blog
        </Heading>
        <p className="text-center text-neutral-600 text-lg mb-16">
          Travel stories, tips, and culinary adventures
        </p>
        <div className="space-y-8">
          {posts.map((post, i) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              // Same deal as the blog index cards: start the article loading on
              // intent, and hand it the summary to paint from.
              onMouseEnter={() => prefetchBlogPost(post.slug)}
              onFocus={() => prefetchBlogPost(post.slug)}
              state={blogPostLinkState(post)}
              className={`group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all grid grid-cols-1 ${
                post.cover_image_url ? 'md:grid-cols-2' : ''
              }`}
            >
              {post.cover_image_url && (
                <div
                  className={`relative overflow-hidden h-60 md:h-auto md:min-h-80 ${i % 2 === 1 ? 'md:order-2' : ''}`}
                >
                  {/*
                    The slot is half of a 1152px container, so 576 CSS px at the
                    top end and roughly a phone's width below `md`. The stored
                    cover is up to 2560px wide, which is the copy the article
                    lightbox needs and several times what this strip ever paints,
                    so it is asked for at the width it is drawn at.
                  */}
                  <img
                    src={post.cover_image_url}
                    srcSet={blogImageSrcSet(post.cover_image_url, HOME_COVER_WIDTHS)}
                    sizes={HOME_COVER_SIZES}
                    alt={blogCoverAlt(post)}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              )}
              <div className="flex flex-col justify-center p-8 md:p-12">
                {post.categories.length > 0 && (
                  <p className="text-xs font-medium text-accent uppercase tracking-wide mb-3">
                    {post.categories.map((c) => c.name).join(' · ')}
                  </p>
                )}
                <Heading level={3} size="h3" weight="semibold" color="primary" className="mb-3 group-hover:text-accent transition-colors">
                  {post.title}
                </Heading>
                {post.excerpt && (
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                )}
                <span className="text-accent font-medium text-sm uppercase tracking-wide">
                  Read More &rarr;
                </span>
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link
            to="/blog"
            className="inline-block px-8 py-3.5 border-2 border-primary text-primary text-sm font-medium uppercase tracking-wide rounded hover:bg-primary hover:text-white transition-all"
          >
            View All Posts
          </Link>
        </div>
      </div>
    </section>
  );
}

/**
 * Its own section rather than a block inside BlogPreview: that section hides
 * itself when there are no published posts, which would take the signup with it.
 * No rule of its own: the WanderNerd band above ends in a gold hairline, and a
 * grey one here would double it.
 *
 * A strip, not a section: the secondary ask (the primary CTA is /plan) gets the
 * form's `row` layout and tight padding so it reads as a rule between the two
 * bands it sits between rather than a full screen of its own.
 */
function Newsletter() {
  return (
    <section className="py-12 bg-neutral-50">
      <div className="max-w-[1200px] mx-auto px-6">
        <NewsletterForm variant="light" source="homepage" layout="row" />
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="py-24 bg-primary text-center">
      <div className="max-w-[1200px] mx-auto px-6">
        <Heading level={2} size="h1" weight="semibold" color="inverse" className="mb-4">
          Ready to Plan Your Next Trip?
        </Heading>
        <p className="text-white/80 text-lg max-w-[600px] mx-auto mb-8 leading-relaxed">
          Tell me what you’re thinking and I’ll take it from there.
        </p>
        <Link
          to="/plan"
          onClick={() => trackEvent('funnel-cta-click', { location: 'bottom-cta' })}
          className="inline-block px-12 py-4.5 bg-accent text-primary text-base font-medium uppercase tracking-wide rounded hover:bg-accent-hover hover:-translate-y-0.5 transition-all"
        >
          Start Planning
        </Link>
        <p className="text-white/60 text-sm mt-4 leading-6 max-w-[540px] mx-auto">
          “Somewhere warm, sometime in March” is plenty to start with.
        </p>
      </div>
    </section>
  );
}

function App() {
  // The prerender build renders the blog band into the HTML, so it's in the
  // first paint and visible to crawlers that don't run JS. Hydration has to
  // start from those same posts or React would throw the server's markup away;
  // the fetch below then refreshes them for anything published since the build.
  const { posts: ssrPosts } = useContext(HomeDataContext);
  const [blogPosts, setBlogPosts] = useState<BlogPostSummary[]>(
    () => ssrPosts ?? readPrerenderedHomePosts() ?? []
  );

  useEffect(() => {
    applyHomeSeo();
    api.getBlogPosts({ page: 1, per_page: HOME_BLOG_POST_COUNT })
      .then((data) => setBlogPosts(data.posts))
      .catch(() => {});
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col bg-white">
      {/* Same chrome as the blog: the site header above, the site footer below,
          the page's own sections in between. The footer's CTA is off because
          the section right above it is already the ask. */}
      <SiteHeader overlay />
      <main className="flex-1">
        <Hero />
        <About />
        <PlanningSteps />
        <Experiences />
        <BlogPreview posts={blogPosts} />
        <WanderNerdTools />
        <Newsletter />
        {/* Renders nothing until there are approved testimonials to show; the
            words themselves live in config/travelerProof.ts. Sits here, right
            before the closing ask, because proof is what the ask stands on. */}
        <TravelerStories />
        <CTA />
      </main>
      <BlogFooter showNewsletter={false} showCta={false} />
    </div>
  );
}

export default App;
